import { useContext } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { apiGet, apiPost, apiPatch } from 'api';
import AuthContext from 'context/auth-context';

const getMunicipalities = async (token, params = {}) => {
  const url = 'admin/municipalities';
  const data = await apiGet({ url, token, params });
  return data;
};

const getMunicipality = async (token, id) => {
  const url = `admin/municipalities/${id}`;
  const data = await apiGet({ url, token });
  return data;
};

// Lightweight id/name list for populating filter dropdowns. Returns a bare
// array (not the paginated { data, meta } shape) thanks to as_options.
const getMunicipalityOptions = async (token) => {
  const url = 'admin/municipalities';
  const data = await apiGet({ url, token, params: { as_options: true } });
  return data;
};

const createMunicipality = async (token, params) => {
  const url = 'admin/municipalities';
  const data = await apiPost({ url, token, objectName: 'municipality', params });
  return data;
};

const updateMunicipality = async (token, id, params) => {
  const url = `admin/municipalities/${id}`;
  const data = await apiPatch({ url, token, objectName: 'municipality', params });
  return data;
};

const getMunicipalityUsers = async (token, municipalityId) => {
  const url = `admin/municipalities/${municipalityId}/users`;
  const data = await apiGet({ url, token });
  return data;
};

const runBasePrepLambdas = async (token, municipalityId) => {
  const url = `admin/municipalities/${municipalityId}/run_base_prep_lambdas`;
  const data = await apiPost({ url, token, objectName: 'municipality', params: {} });
  return data;
};

const runBasePrepImports = async (token, municipalityId) => {
  const url = `admin/municipalities/${municipalityId}/run_base_prep_imports`;
  const data = await apiPost({ url, token, objectName: 'municipality', params: {} });
  return data;
};

const runFullRefresh = async (token, municipalityId) => {
  const url = `admin/municipalities/${municipalityId}/run_full_refresh`;
  const data = await apiPost({ url, token, objectName: 'municipality', params: {} });
  return data;
};

export function useAdminMunicipalities({ params = {}, options } = {}) {
  const token = useContext(AuthContext);
  const queryFn = () => getMunicipalities(token, params);
  const queryKey = ['admin-municipalities', JSON.stringify(params)];
  return useQuery(queryKey, queryFn, { keepPreviousData: true, ...options });
}

export function useAdminMunicipality({ id, options } = {}) {
  const token = useContext(AuthContext);
  const queryFn = () => getMunicipality(token, id);
  const queryKey = ['admin-municipality', id];
  return useQuery(queryKey, queryFn, { enabled: !!id, ...options });
}

export function useAdminMunicipalityOptions({ options } = {}) {
  const token = useContext(AuthContext);
  const queryFn = () => getMunicipalityOptions(token);
  const queryKey = ['admin-municipality-options'];
  return useQuery(queryKey, queryFn, { staleTime: 5 * 60 * 1000, ...options });
}

export function useCreateAdminMunicipality({ options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    (params) => createMunicipality(token, params),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries('admin-municipalities');
        if (options?.onSuccess) {
          options.onSuccess(data);
        }
      }
    }
  );
}

export function useUpdateAdminMunicipality({ id, options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    (params) => updateMunicipality(token, id, params),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries('admin-municipalities');
        queryClient.invalidateQueries(['admin-municipality', id]);
        if (options?.onSuccess) {
          options.onSuccess(data);
        }
      }
    }
  );
}

export function useAdminMunicipalityUsers({ municipalityId, options } = {}) {
  const token = useContext(AuthContext);
  const queryFn = () => getMunicipalityUsers(token, municipalityId);
  const queryKey = ['admin-municipality-users', municipalityId];
  return useQuery(queryKey, queryFn, { enabled: !!municipalityId, ...options });
}

export function useRunBasePrepLambdas({ municipalityId, options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    () => runBasePrepLambdas(token, municipalityId),
    {
      ...options,
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(['admin-municipality-data-updates', municipalityId]);
        if (options?.onSuccess) {
          options.onSuccess(data, variables);
        }
      }
    }
  );
}

export function useRunBasePrepImports({ municipalityId, options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    () => runBasePrepImports(token, municipalityId),
    {
      ...options,
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(['admin-municipality-data-updates', municipalityId]);
        if (options?.onSuccess) {
          options.onSuccess(data, variables);
        }
      }
    }
  );
}

export function useRunFullRefresh({ municipalityId, options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    () => runFullRefresh(token, municipalityId),
    {
      ...options,
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(['admin-municipality-data-updates', municipalityId]);
        if (options?.onSuccess) {
          options.onSuccess(data, variables);
        }
      }
    }
  );
}
